/**
 * IdeaActivity Component
 * 
 * Activity feed showing all changes to an idea.
 * Audit trail for transparency.
 */

import React from 'react';
import { View, Text, StyleSheet, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { color, text, space, radius, icon } from '../../../shared/tokens/mobile';
import { IdeaActivity as IdeaActivityType, IdeaActivityAction } from '../../../shared/types/idea';

interface IdeaActivityProps {
  activities: IdeaActivityType[];
  loading: boolean;
}

const ACTION_CONFIG: Record<IdeaActivityAction, { icon: string; label: string; color: string }> = {
  created: { icon: 'add-circle-outline', label: 'created this idea', color: color.success },
  updated: { icon: 'pencil-outline', label: 'updated the idea', color: color.info },
  status_changed: { icon: 'swap-horizontal-outline', label: 'changed status', color: color.warning },
  voted: { icon: 'thumbs-up-outline', label: 'voted', color: color.primary },
  commented: { icon: 'chatbubble-outline', label: 'commented', color: color.info },
  mentioned: { icon: 'at-outline', label: 'mentioned someone', color: color.primary },
  resolved: { icon: 'checkmark-circle-outline', label: 'resolved', color: color.success },
  reopened: { icon: 'refresh-outline', label: 'reopened', color: color.warning },
};

export function IdeaActivity({ activities, loading }: IdeaActivityProps) {
  if (loading && activities.length === 0) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="small" color={color.primary} />
      </View>
    );
  }

  if (activities.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <Ionicons name="time-outline" size={icon.xl} color={color.textMuted} />
        <Text style={styles.emptyText}>No activity yet</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {activities.map((activity, index) => (
        <ActivityItem 
          key={activity.id} 
          activity={activity} 
          isLast={index === activities.length - 1}
        />
      ))}
    </View>
  );
}

// =============================================================================
// ACTIVITY ITEM
// =============================================================================

interface ActivityItemProps {
  activity: IdeaActivityType;
  isLast: boolean;
}

function ActivityItem({ activity, isLast }: ActivityItemProps) {
  const config = ACTION_CONFIG[activity.action] || ACTION_CONFIG.updated;
  const userName = activity.user?.display_name || activity.user?.email?.split('@')[0] || 'Someone';
  const timeAgo = getTimeAgo(new Date(activity.created_at));
  
  // Format details based on action type
  const details = formatDetails(activity);

  return (
    <View style={styles.activityItem}>
      {/* Timeline connector */}
      {!isLast && <View style={styles.timelineConnector} />}
      
      {/* Icon */}
      <View style={[styles.iconContainer, { backgroundColor: config.color + '15' }]}>
        <Ionicons name={config.icon as any} size={14} color={config.color} />
      </View>
      
      {/* Content */}
      <View style={styles.activityContent}>
        <Text style={styles.activityText}>
          <Text style={styles.activityUser}>{userName}</Text>
          {' '}
          <Text style={styles.activityAction}>{config.label}</Text>
          {details && <Text style={styles.activityDetails}> {details}</Text>}
        </Text>
        <Text style={styles.activityTime}>{timeAgo}</Text>
      </View>
    </View>
  );
}

// =============================================================================
// HELPERS
// =============================================================================

function formatDetails(activity: IdeaActivityType): string | null {
  if (!activity.details) return null;
  
  const d = activity.details as Record<string, unknown>;
  
  switch (activity.action) {
    case 'status_changed':
      return `from ${formatStatus(d.old_status as string)} to ${formatStatus(d.new_status as string)}`;
    case 'voted':
      return d.vote_type === 'up' ? '👍' : '👎';
    default:
      return null;
  }
}

function formatStatus(status: string): string {
  return status?.replace(/_/g, ' ') || 'unknown';
}

function getTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

// =============================================================================
// STYLES
// =============================================================================

const styles = StyleSheet.create({
  container: {
    paddingLeft: space.xs,
  },
  loadingContainer: {
    padding: space.xl,
    alignItems: 'center',
  },
  emptyContainer: {
    alignItems: 'center',
    paddingVertical: space.xl,
  },
  emptyText: {
    fontSize: text.body.fontSize,
    color: color.textMuted,
    marginTop: space.sm,
  },
  
  // Activity item
  activityItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: space.sm,
    paddingBottom: space.md,
    position: 'relative',
  },
  timelineConnector: {
    position: 'absolute',
    left: 12,
    top: 28,
    bottom: 0,
    width: 2,
    backgroundColor: color.divider,
  },
  iconContainer: {
    width: 26,
    height: 26,
    borderRadius: 13,
    alignItems: 'center',
    justifyContent: 'center',
  },
  activityContent: {
    flex: 1,
    paddingTop: 3,
  },
  activityText: {
    fontSize: text.caption.fontSize,
    lineHeight: text.caption.lineHeight * 1.2,
  },
  activityUser: {
    fontWeight: '600',
    color: color.textPrimary,
  },
  activityAction: {
    color: color.textSecondary,
  },
  activityDetails: {
    color: color.textMuted,
    fontStyle: 'italic',
  },
  activityTime: {
    fontSize: 11,
    color: color.textMuted,
    marginTop: 2,
  },
});

